import React, { useEffect, useRef, useState } from "react";

/**
 * HtmlEditor — vanilla contentEditable WYSIWYG
 * Props:
 *  - value: string (HTML)
 *  - onChange(html: string)
 *  - minHeight: number (px)
 */
export default function HtmlEditor({ value = "", onChange, minHeight = 160 }) {
  const editorRef = useRef(null);
  const [internal, setInternal] = useState(value);

  // keep internal html in sync when parent value changes
  useEffect(() => {
    setInternal(value || "");
  }, [value]);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== internal) {
      editorRef.current.innerHTML = internal || "";
    }
  }, [internal]);

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    // trigger change
    handleInput();
  };

  const handleInput = () => {
    const html = editorRef.current?.innerHTML ?? "";
    setInternal(html);
    onChange && onChange(html);
  };

  const handleKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey) {
      if (e.key.toLowerCase() === "b") { e.preventDefault(); exec("bold"); }
      if (e.key.toLowerCase() === "i") { e.preventDefault(); exec("italic"); }
      if (e.key.toLowerCase() === "u") { e.preventDefault(); exec("underline"); }
      if (e.key.toLowerCase() === "s") { e.preventDefault(); /* capture if needed */ }
    }
  };

  const makeLink = () => {
    const url = window.prompt("Enter URL (https://…)", "https://");
    if (!url) return;
    exec("createLink", url);
  };

  const insertImageByUrl = () => {
    const url = window.prompt("Image URL (https://…jpg/png/webp)");
    if (!url) return;
    exec("insertImage", url);
  };

  const clearFormatting = () => {
    exec("removeFormat");
  };

  return (
    <div className="w-full">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 rounded-t-md border border-gray-200 bg-gray-50 px-2 py-1">
        <select
          onChange={(e) => exec("formatBlock", e.target.value)}
          className="border border-gray-200 rounded px-2 py-1 text-sm bg-white"
          defaultValue="p"
          title="Paragraph / Heading"
        >
          <option value="p">Paragraph</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="h4">Heading 4</option>
        </select>

        <button onClick={() => exec("bold")} className="px-2 py-1 text-sm hover:bg-gray-200 rounded" title="Bold">B</button>
        <button onClick={() => exec("italic")} className="px-2 py-1 text-sm hover:bg-gray-200 rounded italic" title="Italic">I</button>
        <button onClick={() => exec("underline")} className="px-2 py-1 text-sm hover:bg-gray-200 rounded" title="Underline">U</button>
        <span className="mx-1 h-5 w-px bg-gray-300" />

        <button onClick={() => exec("insertUnorderedList")} className="px-2 py-1 text-sm hover:bg-gray-200 rounded" title="Bulleted list">• List</button>
        <button onClick={() => exec("insertOrderedList")} className="px-2 py-1 text-sm hover:bg-gray-200 rounded" title="Numbered list">1. List</button>
        <span className="mx-1 h-5 w-px bg-gray-300" />

        <button onClick={() => exec("justifyLeft")} className="px-2 py-1 text-sm hover:bg-gray-200 rounded" title="Align left">⯇</button>
        <button onClick={() => exec("justifyCenter")} className="px-2 py-1 text-sm hover:bg-gray-200 rounded" title="Center">≡</button>
        <button onClick={() => exec("justifyRight")} className="px-2 py-1 text-sm hover:bg-gray-200 rounded" title="Align right">⯈</button>
        <span className="mx-1 h-5 w-px bg-gray-300" />

        <button onClick={makeLink} className="px-2 py-1 text-sm hover:bg-gray-200 rounded" title="Insert link">🔗</button>
        <button onClick={() => exec("unlink")} className="px-2 py-1 text-sm hover:bg-gray-200 rounded" title="Remove link">❌🔗</button>

        <button onClick={insertImageByUrl} className="px-2 py-1 text-sm hover:bg-gray-200 rounded" title="Insert image by URL">🖼️</button>

        <span className="mx-1 h-5 w-px bg-gray-300" />
        <button onClick={clearFormatting} className="px-2 py-1 text-xs hover:bg-gray-200 rounded" title="Clear formatting">Clear</button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        className="rounded-b-md border border-t-0 border-gray-200 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-green-600"
        style={{ minHeight }}
      />
    </div>
  );
}
